import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import Confetti from 'react-confetti';
import { applyOpacity } from '../../utils/color.utils';
import { CloseContainer } from './HBModalCloseButton';
import { hideInPrint } from '../../styles/mixins';
import HBField from '../HBField/HBField';
import HBText from '../HBText/HBText';
import { mediumUp } from '../../styles/breakpoints';
import { TTextAlignment } from '../HBText/HBText.types';
import { getAlignment } from '../../utils/alignment.utils';
import spacing from '../../styles/spacing';
import { inertAttr } from '../../utils/shared.utils';
import { HTMLMotionProps } from 'framer-motion';
import { customScrollBar } from '../../styles/scrollbar';
import { Z_INDEX } from '../../styles/variables';
import { vars } from '../../theme';

export const ALMOST_FULL_SCREEN_GAP = 12;

export const Container = styled(motion.div)<{
    $hasParent: boolean;
    $forceMobile: boolean;
    $noInnerScroll: boolean;
    $blurBackdrop?: boolean;
    $isAiModal?: boolean;
}>`
    font-family: ${vars.fonts.body};
    position: fixed;
    display: flex;
    inset: 0;
    justify-content: center;
    align-items: center;
    background-color: ${applyOpacity(
        vars.colors.surface.containerInverted,
        70
    )};
    z-index: ${Z_INDEX.POPOVER}; // fixes angular z-index

    ${({ $noInnerScroll, $hasParent, $forceMobile, $blurBackdrop }) => css`
        ${$noInnerScroll &&
        css`
            padding-top: 40px;
            align-items: initial;
            display: inline-grid;
            overflow: auto;

            @media print {
                position: static;
                display: block;
                inset: unset;
                padding-top: unset;
                overflow: unset;
            }
        `}
        ${$hasParent &&
        css`
            position: absolute;
        `}
        ${$forceMobile &&
        css`
            position: absolute;
            max-width: 310px;
            max-height: 650px;
            margin: auto;
            border-radius: ${vars.radius.xs};
            z-index: 2;
        `}
        ${$blurBackdrop &&
        css`
            backdrop-filter: blur(10px);
        `}
    `}

    @media print {
        background: ${vars.colors.surface.container};
    }
`;

export const PaddingDiv = styled.div`
    height: 40px;
    ${hideInPrint};
`;

export const Modal = styled(motion.div).attrs(inertAttr)<{
    $maxWidth: string;
    $maxHeight: string;
    $noInnerScroll: boolean;
    $isAlmostFullScreen: boolean;
    $isMediumUp: boolean;
    $isFullViewport?: boolean;
    $isAiModal: boolean;
}>`
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: ${({ $maxWidth }) => $maxWidth};
    max-height: ${({ $maxHeight }) => $maxHeight};
    background: ${vars.colors.surface.container};
    z-index: ${Z_INDEX.MODAL};
    border-radius: 0;
    overflow: hidden;

    ${({
        $isFullViewport,
        $isMediumUp,
        $noInnerScroll,
        $isAlmostFullScreen
    }) => css`
        ${$isFullViewport &&
        css`
            height: 100%;
            width: 100vw;
        `}
        ${$isMediumUp &&
        css`
            overflow: initial;
            min-height: initial;
            min-width: 300px;
            border-radius: ${vars.radius.xs};
        `}
        ${$noInnerScroll &&
        css`
            max-height: initial;

            @media print {
                max-width: initial;
            }
        `}
        ${$isAlmostFullScreen &&
        css`
            min-height: calc(100vh - ${2 * ALMOST_FULL_SCREEN_GAP}px);
            min-width: calc(100vw - ${2 * ALMOST_FULL_SCREEN_GAP}px);
        `}
    `}
`;

export const Header = styled(motion.div)<{
    $isEmptyTitle: boolean;
}>`
    display: flex;
    align-items: center;
    position: relative;
    min-height: 60px;
    padding: 0 20px;
    gap: 8px;
    color: ${vars.colors.text.primary};
    ${({ $isEmptyTitle }) =>
        !$isEmptyTitle &&
        css`
            border-bottom: 1px solid ${vars.colors.stroke.secondary};
        `}
    font-size: 18px;
    font-weight: 600;
`;

export const HeaderTitle = styled.span<{ $alignment: TTextAlignment }>`
    flex: 1;
    min-width: 0;
    text-align: ${({ $alignment }) => $alignment};
`;

export const StyledHeaderButtonText = styled(HBText)`
    margin-left: 8px;
`;

export const HeaderButton = styled.div`
    display: flex;
    align-items: center;
`;

export const HeaderActionsContainer = styled.span`
    display: flex;
    align-items: center;
    gap: ${spacing[8]};
    cursor: pointer;

    ${hideInPrint}
`;

const icon = css`
    position: absolute;
    top: 16px;
    height: 24px;
    cursor: pointer;

    ${hideInPrint}
`;

export const Icon = styled.span`
    ${icon}
    right: 16px;
`;

export const BackIcon = styled.span`
    ${icon}
    left: 16px;
`;

export const HeaderLeftSection = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    ${BackIcon} {
        position: static;
    }
`;

export const HeaderRightSection = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    ${CloseContainer} {
        position: static;
    }
`;

export const Body = styled.div<{
    $hasActions: boolean;
    $noInnerScroll: boolean;
    $isAlmostFullScreen: boolean;
    $isEmptyTitle: boolean;
}>`
    overflow-y: auto;
    flex: 1;
    padding: ${({ $hasActions }) =>
        $hasActions ? '32px 32px 0px 32px' : '32px'};
    ${customScrollBar}

    ${({ $isEmptyTitle, $noInnerScroll, $isAlmostFullScreen }) => css`
        ${$isEmptyTitle &&
        css`
            padding-top: 0;
        `}
        ${$noInnerScroll &&
        css`
            overflow-y: visible;
        `}
        ${$isAlmostFullScreen &&
        css`
            padding: 0;
        `}
    `}
`;

export const Actions = styled.div<{
    $isMediumUp: boolean;
}>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    background: ${vars.colors.surface.container};
    box-sizing: border-box;
    width: 100%;

    ${({ $isMediumUp }) =>
        $isMediumUp &&
        css`
            width: initial;
            flex-direction: row;
        `}

    ${hideInPrint}
`;

export const CheckboxAndActions = styled.div<{
    $hasCheckbox: boolean;
    $actionsAlignment: TTextAlignment;
    $forceMobile: boolean;
}>`
    display: flex;
    ${({ $actionsAlignment }) => getAlignment($actionsAlignment)}

    padding: 32px;
    ${({ $hasCheckbox, $forceMobile }) =>
        $hasCheckbox &&
        css`
            flex-direction: column;

            ${!$forceMobile &&
            css`
                ${mediumUp} {
                    flex-direction: row;
                }
            `}
        `}
`;

export const StyledHBField = styled(HBField)`
    flex-direction: row-reverse;
    justify-content: flex-end;

    label {
        font-size: 14px;
        margin-left: 8px;
    }

    ${mediumUp} {
        label {
            font-size: 12px;
        }
    }
`;

export const FooterExtraContent = styled.div`
    align-self: center;
    margin-right: auto;
`;

export const CheckboxContainer = styled.div``;

export const ActionSpacer = styled.div<{
    $isMediumUp?: boolean;
}>`
    width: 100%;
    margin-top: 12px;
    ${({ $isMediumUp }) =>
        $isMediumUp &&
        css`
            width: initial;
            margin-top: initial;
            &:not(:last-child) {
                margin-right: 8px;
            }
        `}
`;

export const StyledConfetti = styled(Confetti)``;

ActionSpacer.defaultProps = {
    $isMediumUp: true
};

export const ChildModalOverlay = styled(motion.div)`
    position: absolute;
    width: 100%;
    height: 100%;
    z-index: 1000;
    background-color: ${applyOpacity(vars.colors.surface.page, 90)};
    border-radius: ${vars.radius.xs};
`;
export const MODAL_ANIMATION: Pick<
    HTMLMotionProps<'div'>,
    'initial' | 'animate' | 'exit' | 'transition'
> = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.15 }
};
